// src/components/common/Header.jsx
import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaBars, 
  FaSearch, 
  FaBell, 
  FaEnvelope, 
  FaHome, 
  FaUserFriends, 
  FaPlus,
  FaSignOutAlt,
  FaUserCog
} from 'react-icons/fa';
import { AiOutlineClose } from 'react-icons/ai';

import { UserContext } from '../../context/userContext';
import { NotificationContext } from '../../context/notificationContext';
import { MessageContext } from '../../context/messageContext';

import Logo from "../../assets/images/logo1.png";
import Avatar from '../ui/Avatar';
import Notifications from '../social/Notifications';

const Header = () => {
  const [isNavShowing, setIsNavShowing] = useState(window.innerWidth > 800);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  
  const { currentUser } = useContext(UserContext);
  const { unreadCount: notificationCount } = useContext(NotificationContext);
  const { unreadCount: messageCount } = useContext(MessageContext);
  
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 800) {
        setIsNavShowing(true);
      } else {
        setIsNavShowing(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleNav = () => {
    setIsNavShowing(prev => !prev);
  };

  const closeNavHandler = () => {
    if (window.innerWidth < 800) {
      setIsNavShowing(false);
    }
  };

  const toggleSearch = () => {
    setShowSearch(prev => !prev);
    if (!showSearch) {
      setTimeout(() => {
        document.getElementById('search-input')?.focus();
      }, 100);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
      setShowSearch(false);
      setSearchTerm('');
    }
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(prev => !prev);
  };

  useEffect(() => {
    const closeProfileMenu = (e) => {
      if (showProfileMenu && !e.target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('click', closeProfileMenu);
    return () => document.removeEventListener('click', closeProfileMenu);
  }, [showProfileMenu]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 h-16 bg-white shadow-sm backdrop-blur-md bg-opacity-90">
      <div className="container mx-auto h-full px-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="h-9 w-auto" onClick={closeNavHandler}>
          <img src={Logo} alt="Blog Logo" className="h-full w-auto" />
        </Link>

        {/* Search form - Desktop */}
        <div className={`hidden md:block relative ${showSearch ? 'w-64' : 'w-0'} transition-all duration-300 overflow-hidden`}>
          <form onSubmit={handleSearch} className="w-full">
            <input
              id="search-input"
              type="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button 
              type="submit" 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
            >
              <FaSearch />
            </button>
          </form>
        </div>

        {/* Mobile search form - shows as overlay */}
        {showSearch && (
          <div className="md:hidden fixed inset-0 bg-white z-50 p-4">
            <form onSubmit={handleSearch} className="relative">
              <input
                id="search-input-mobile"
                type="search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button 
                type="submit" 
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                <FaSearch />
              </button>
            </form>
            <button 
              onClick={toggleSearch}
              className="absolute top-4 right-4 text-gray-700 text-xl"
            >
              <AiOutlineClose />
            </button>
          </div>
        )}

        {/* Navigation Menu */}
        <div className={`${isNavShowing ? 'block' : 'hidden'} md:block`}>
          <ul className="flex flex-col md:flex-row md:items-center md:space-x-6 absolute md:relative top-16 md:top-0 left-0 md:left-auto w-full md:w-auto bg-white md:bg-transparent shadow-lg md:shadow-none z-10 py-3 md:py-0">
            <li>
              <Link 
                to="/" 
                onClick={closeNavHandler}
                className="flex items-center px-6 md:px-0 py-3 md:py-0 text-gray-700 hover:text-primary transition-colors"
              >
                <FaHome className="mr-2" /> <span>Home</span>
              </Link>
            </li>
            
            {currentUser?.id && (
              <>
                <li>
                  <Link 
                    to="/feed" 
                    onClick={closeNavHandler}
                    className="flex items-center px-6 md:px-0 py-3 md:py-0 text-gray-700 hover:text-primary transition-colors"
                  >
                    <FaUserFriends className="mr-2" /> <span>Feed</span>
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/create" 
                    onClick={closeNavHandler}
                    className="flex items-center px-6 md:px-0 py-3 md:py-0 text-gray-700 hover:text-primary transition-colors"
                  >
                    <FaPlus className="mr-2" /> <span>Create</span>
                  </Link>
                </li>
              </>
            )}
            
            <li>
              <Link 
                to="/authors" 
                onClick={closeNavHandler}
                className="flex items-center px-6 md:px-0 py-3 md:py-0 text-gray-700 hover:text-primary transition-colors"
              >
                <span>Authors</span>
              </Link>
            </li>
            
            {/* Desktop-only search toggle */}
            <li className="hidden md:block">
              <button 
                onClick={toggleSearch} 
                className="text-gray-700 hover:text-primary transition-colors"
              >
                <FaSearch />
              </button>
            </li>
            
            {currentUser?.id ? (
              <>
                {/* Notifications */}
                <li className="px-6 md:px-0 py-2 md:py-0">
                  <Notifications />
                </li>
                
                {/* Messages */}
                <li className="px-6 md:px-0 py-2 md:py-0">
                  <Link 
                    to="/messages" 
                    className="relative inline-block text-gray-700 hover:text-primary transition-colors"
                  >
                    <FaEnvelope />
                    {messageCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {messageCount}
                      </span>
                    )}
                  </Link>
                </li>
                
                {/* User profile - Desktop */}
                <li className="hidden md:block relative profile-menu-container">
                  <button 
                    onClick={toggleProfileMenu}
                    className="flex items-center space-x-2 focus:outline-none"
                  >
                    <Avatar 
                      user={{
                        _id: currentUser.id,
                        name: currentUser.name,
                        avatar: currentUser.avatar
                      }} 
                      size="sm" 
                      linkToProfile={false} 
                    />
                  </button>
                  
                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                      <Link 
                        to={`/profile/${currentUser.id}`} 
                        onClick={() => setShowProfileMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <FaUserCog className="mr-2" /> Profile
                      </Link>
                      <Link 
                        to="/dashboard" 
                        onClick={() => setShowProfileMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Dashboard
                      </Link>
                      <Link 
                        to="/logout" 
                        onClick={() => setShowProfileMenu(false)}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <FaSignOutAlt className="mr-2" /> Logout
                      </Link>
                    </div>
                  )}
                </li>
                
                {/* User profile - Mobile */}
                <li className="md:hidden px-6 py-3">
                  <Link 
                    to={`/profile/${currentUser.id}`} 
                    onClick={closeNavHandler}
                    className="flex items-center text-gray-700"
                  >
                    <FaUserCog className="mr-2" /> Profile
                  </Link>
                </li>
                <li className="md:hidden px-6 py-3">
                  <Link 
                    to="/dashboard" 
                    onClick={closeNavHandler}
                    className="flex items-center text-gray-700"
                  >
                    Dashboard
                  </Link>
                </li>
                <li className="md:hidden px-6 py-3">
                  <Link 
                    to="/logout" 
                    onClick={closeNavHandler}
                    className="flex items-center text-gray-700"
                  >
                    <FaSignOutAlt className="mr-2" /> Logout
                  </Link>
                </li>
              </>
            ) : (
              <li className="px-6 md:px-0 py-3 md:py-0">
                <Link 
                  to="/login" 
                  onClick={closeNavHandler}
                  className="md:inline-block md:px-4 md:py-2 md:bg-primary md:text-white md:rounded-md md:font-medium md:hover:bg-opacity-90 transition-colors"
                >
                  Login
                </Link>
              </li>
            )}
          </ul>
        </div>

        {/* Mobile search toggle & menu button */}
        <div className="md:hidden flex items-center gap-4">
          <button 
            onClick={toggleSearch} 
            className="text-gray-700 hover:text-primary text-xl"
          >
            <FaSearch />
          </button>
          <button 
            className="text-gray-700 hover:text-primary text-xl focus:outline-none" 
            onClick={toggleNav}
          >
            {isNavShowing ? <AiOutlineClose /> : <FaBars />}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Header;